import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Box, Typography, TextField, Button, Card, CardContent,
    Grid, Alert, CircularProgress, useTheme
} from '@mui/material';
import {
    Save, Phone, Email, Instagram, LinkedIn,
    KeyboardArrowLeft, AccessTime
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { contactInfoAPI } from '../services/api';
import AdminHeader from '../components/AdminHeader';

const ContactsManage = () => {
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();

    const [formData, setFormData] = useState({
        phone: '',
        emailPrimary: '',
        emailSecondary: '',
        facebookUrl: '', // 👈 Ова ќе го користиме за Instagram 2
        instagramUrl: '', // 👈 Instagram 1
        linkedinUrl: '',
        workingHours: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (!authLoading && (!isAuthenticated || user?.role !== 'ADMIN')) {
            navigate('/login');
        }
    }, [authLoading, isAuthenticated, user, navigate]);

    useEffect(() => { fetchContactInfo(); }, []);

    const fetchContactInfo = async () => {
        try {
            setLoading(true);
            const data = await contactInfoAPI.admin.get();
            if (data) {
                setFormData({
                    phone: data.phone || '',
                    emailPrimary: data.emailPrimary || '',
                    emailSecondary: data.emailSecondary || '',
                    facebookUrl: data.facebookUrl || '',
                    instagramUrl: data.instagramUrl || '',
                    linkedinUrl: data.linkedinUrl || '',
                    workingHours: data.workingHours || ''
                });
            }
        } catch (err) {
            setError('Грешка при вчитување на контакт информациите');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field) => (e) => {
        setFormData({ ...formData, [field]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError('');
            await contactInfoAPI.admin.update(formData);
            setSuccess('Контакт информациите се успешно зачувани!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Грешка при зачувување на контакт информациите');
        } finally {
            setSaving(false);
        }
    };

    if (authLoading || loading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                backgroundColor: theme.palette.background.default,
                display: 'flex', justifyContent: 'center', alignItems: 'center'
            }}>
                <CircularProgress sx={{ color: '#ff7eb9' }} />
                <Typography variant="h6" sx={{ ml: 2, color: theme.palette.text.primary }}>
                    Вчитување...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 4, minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
            <Container maxWidth="lg">
                <AdminHeader />

                <Box sx={{ mb: 4 }}>
                    <Button
                        onClick={() => navigate('/admin/sections')}
                        startIcon={<KeyboardArrowLeft />}
                        variant="outlined"
                        sx={{
                            color: '#ff7eb9', borderColor: '#ff7eb9',
                            fontWeight: 'bold', px: 3, py: 1, borderRadius: '12px',
                            '&:hover': {
                                backgroundColor: 'rgba(255,126,185,0.1)',
                                borderColor: '#ff4a97', transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Назад кон секции
                    </Button>
                </Box>

                <Box sx={{ mb: 6, textAlign: 'center' }}>
                    <Box sx={{
                        width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 3,
                        background: 'linear-gradient(135deg, #ff7eb9, #AB47BC)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 8px 32px rgba(255,126,185,0.35)',
                        fontSize: '2rem'
                    }}>
                        📞
                    </Box>
                    <Typography variant="h2" sx={{
                        fontWeight: 900,
                        background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1.5, fontSize: { xs: '2rem', md: '3rem' }
                    }}>
                        Контакти и Социјални мрежи
                    </Typography>
                    <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                        Уредете ги контакт информациите и линковите за социјални мрежи
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>{success}</Alert>}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={4}>

                        {/* Телефон */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', borderRadius: '20px', backgroundColor: theme.palette.background.paper, border: '1px solid rgba(255,126,185,0.2)' }}>
                                <Box sx={{ height: 5, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)' }} />
                                <CardContent sx={{ p: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        <Phone sx={{ color: '#ff7eb9' }} />
                                        <Typography variant="h5" fontWeight="bold">Телефон</Typography>
                                    </Box>
                                    <TextField fullWidth label="Телефонски број" value={formData.phone} onChange={handleChange('phone')} placeholder="+389 70 000 000" />
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Е-маил */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', borderRadius: '20px', backgroundColor: theme.palette.background.paper, border: '1px solid rgba(255,126,185,0.2)' }}>
                                <Box sx={{ height: 5, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)' }} />
                                <CardContent sx={{ p: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        <Email sx={{ color: '#ff7eb9' }} />
                                        <Typography variant="h5" fontWeight="bold">Е-маил адреси</Typography>
                                    </Box>
                                    <TextField fullWidth label="Примарен е-маил" value={formData.emailPrimary} onChange={handleChange('emailPrimary')} sx={{ mb: 2 }} />
                                    <TextField fullWidth label="Секундарен е-маил" value={formData.emailSecondary} onChange={handleChange('emailSecondary')} />
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Социјални мрежи */}
                        {/* ===== Социјални мрежи ===== */}
                        <Grid item xs={12}>
                            <Card sx={{
                                borderRadius: '20px',
                                backgroundColor: theme.palette.background.paper,
                                border: '1px solid rgba(255,126,185,0.2)',
                                boxShadow: '0 4px 24px rgba(255,126,185,0.08)',
                                overflow: 'hidden'
                            }}>
                                <Box sx={{ height: 5, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)' }} />
                                <CardContent sx={{ p: 4 }}>
                                    <Typography variant="h5" fontWeight="bold" sx={{
                                        background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        mb: 3
                                    }}>
                                        🌐 Социјални мрежи
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Instagram (Главен)"
                                                value={formData.instagramUrl}
                                                onChange={handleChange('instagramUrl')}
                                                InputProps={{ startAdornment: <Instagram sx={{ mr: 1, color: '#E4405F' }} /> }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                        '&:hover fieldset': { borderColor: '#E4405F' },
                                                        '&.Mui-focused fieldset': { borderColor: '#E4405F' }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Instagram (Секундарен)"
                                                value={formData.facebookUrl} // Мапирано на facebookUrl во базата
                                                onChange={handleChange('facebookUrl')}
                                                InputProps={{ startAdornment: <Instagram sx={{ mr: 1, color: '#833AB4' }} /> }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                        '&:hover fieldset': { borderColor: '#833AB4' },
                                                        '&.Mui-focused fieldset': { borderColor: '#833AB4' }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="LinkedIn URL"
                                                value={formData.linkedinUrl}
                                                onChange={handleChange('linkedinUrl')}
                                                InputProps={{ startAdornment: <LinkedIn sx={{ mr: 1, color: '#0077B5' }} /> }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                        '&:hover fieldset': { borderColor: '#0077B5' },
                                                        '&.Mui-focused fieldset': { borderColor: '#0077B5' }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Работно време */}
                        <Grid item xs={12}>
                            <Card sx={{ borderRadius: '20px', backgroundColor: theme.palette.background.paper, border: '1px solid rgba(255,126,185,0.2)' }}>
                                <Box sx={{ height: 5, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)' }} />
                                <CardContent sx={{ p: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        <AccessTime sx={{ color: '#ff7eb9' }} />
                                        <Typography variant="h5" fontWeight="bold">Работно време</Typography>
                                    </Box>
                                    <TextField fullWidth label="Работно време" value={formData.workingHours} onChange={handleChange('workingHours')} multiline rows={2} />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                            disabled={saving}
                            sx={{
                                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                px: 6, py: 2, fontSize: '1.2rem', fontWeight: 'bold', borderRadius: '14px'
                            }}
                        >
                            {saving ? 'Се зачувува...' : '💾 Зачувај промени'}
                        </Button>
                    </Box>
                </form>
            </Container>
        </Box>
    );
};

export default ContactsManage;